import React, { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { CircularProgress, Container, Typography, useMediaQuery } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import ProductCard from "../components/ProductCard/ProductCard";
import { useHomeProducts } from "../components/hooks/useHomeProducts";
import { useCategoryShowcase } from "../hooks/useCategoryShowcase";
import { Product } from "../types/Product";
import { HttpCache } from "../http/httpCache";
import { Icon, ImgPh, SectionHeader } from "../components/liquid/LiquidPrimitives";
import type { CategoryImage } from "../components/CategoryShowcase/ImageWithOverlay";
import { trackMetric } from "@ecommerce/analytics";

type SnackbarNavState = {
    snackbar?: {
        message: string;
        variant?: "default" | "error" | "success" | "warning" | "info";
    };
};

const trackCategoryClickFromLink = (link: string) => {
    const categorySku = link.match(/^\/?categoria\/([^/?#]+)/)?.[1];
    if (!categorySku) return;

    trackMetric("CATEGORY_CLICK", {
        entitySku: decodeURIComponent(categorySku),
    });
};

function HeroSection({ products }: { products: Product[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");
    const [activeIdx, setActiveIdx] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const heroProducts = products.slice(0, 6);

    useEffect(() => {
        if (heroProducts.length <= 1) return;
        timerRef.current = setInterval(() => {
            setActiveIdx((prev) => (prev + 1) % heroProducts.length);
        }, 3500);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [heroProducts.length]);

    return (
        <section style={{ padding: isSmall ? "0 0 24px" : "0 0 36px" }}>
            <div
                style={{
                    overflow: "hidden",
                    position: "relative",
                    minHeight: isSmall ? 560 : 460,
                    background: "#001f33",
                    display: "grid",
                    gridTemplateColumns: isSmall ? "1fr" : "1fr auto",
                    alignItems: "center",
                    padding: isSmall ? "48px 28px" : "64px 80px",
                    gap: 32,
                }}
            >
                {/* Video de fondo */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                >
                    <source
                        src="https://assets-prod.tindi.com.ar/suscripciones/voda-argentina/content/voda_banner-web-2.mp4"
                        type="video/mp4"
                    />
                </video>

                {/* Overlay para legibilidad del texto sobre el video */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(90deg, rgba(0,15,25,0.78) 0%, rgba(0,31,51,0.50) 55%, rgba(0,31,51,0.18) 100%)",
                        zIndex: 1,
                    }}
                />

                {/* Texto */}
                <div style={{ position: "relative", zIndex: 2 }}>
                    <h1
                        style={{
                            fontSize: isSmall ? 36 : 54,
                            fontWeight: 700,
                            letterSpacing: "-0.03em",
                            lineHeight: 1.05,
                            color: "#ffffff",
                            margin: "0 0 18px",
                            maxWidth: 480,
                        }}
                    >
                        Purificación de agua para tu hogar y empresa
                    </h1>
                    <p
                        style={{
                            fontSize: isSmall ? 14 : 15,
                            color: "rgba(255,255,255,0.55)",
                            lineHeight: 1.7,
                            margin: "0 0 32px",
                            maxWidth: 400,
                        }}
                    >
                        Suscripción mensual de dispensers, purificadores y equipos de última
                        generación. Instalación y mantenimiento incluido.
                    </p>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <a
                            href="/productos"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "14px 26px",
                                background: "#ffffff",
                                color: "#003A5C",
                                borderRadius: 999,
                                fontSize: 14,
                                fontWeight: 700,
                                textDecoration: "none",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
                            }}
                        >
                            ¡Contrata hoy! <Icon.arrowUpRight color="#003A5C" />
                        </a>
                        <a
                            href="/productos"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "14px 26px",
                                background: "rgba(255,255,255,0.10)",
                                color: "#ffffff",
                                borderRadius: 999,
                                border: "1px solid rgba(255,255,255,0.20)",
                                fontSize: 14,
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Conocer más
                        </a>
                    </div>
                </div>

                {/* Card destacada mobile */}
                {isSmall && heroProducts.length > 0 && (
                    <div style={{ marginTop: 20, position: "relative", zIndex: 2 }}>
                        <Link
                            to={`/productos/${heroProducts[activeIdx].sku}`}
                            onClick={() =>
                                trackMetric("PRODUCT_CLICK", {
                                    entityId: heroProducts[activeIdx].id.toString(),
                                })
                            }
                            style={{
                                display: "block",
                                textDecoration: "none",
                                borderRadius: 18,
                                overflow: "hidden",
                                background: "rgba(255,255,255,0.10)",
                                backdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.18)",
                                boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
                            }}
                        >
                            <div style={{ height: 210, background: "#f4f5f7" }}>
                                {heroProducts[activeIdx]?.urlImagenPrincipal ? (
                                    <img
                                        src={heroProducts[activeIdx].urlImagenPrincipal}
                                        alt={heroProducts[activeIdx].nombre}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            background: "#f4f5f7",
                                        }}
                                    />
                                )}
                            </div>
                            <div style={{ padding: "12px 14px" }}>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#ffffff",
                                        letterSpacing: "-0.01em",
                                        textTransform: "uppercase",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {heroProducts[activeIdx]?.nombre}
                                </div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: "#cfe5ff",
                                        marginTop: 4,
                                    }}
                                >
                                    {heroProducts[activeIdx]?.precio
                                        ? `$${heroProducts[activeIdx].precio.toLocaleString("es-AR")}`
                                        : "Consultar precio"}
                                </div>
                            </div>
                        </Link>

                        {/* Thumbnails de navegación */}
                        {heroProducts.length > 1 && (
                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    marginTop: 12,
                                    overflowX: "auto",
                                    scrollbarWidth: "none",
                                    justifyContent: "center",
                                    paddingBottom: 2,
                                }}
                            >
                                {heroProducts.slice(0, 5).map((product, i) => (
                                    <button
                                        key={product.id}
                                        type="button"
                                        onClick={() => setActiveIdx(i)}
                                        style={{
                                            flexShrink: 0,
                                            width: 52,
                                            height: 52,
                                            borderRadius: 12,
                                            overflow: "hidden",
                                            background:
                                                i === activeIdx
                                                    ? "rgba(255,255,255,0.16)"
                                                    : "rgba(255,255,255,0.09)",
                                            border: `2px solid ${i === activeIdx ? "rgba(255,255,255,0.80)" : "rgba(255,255,255,0.20)"}`,
                                            transform: i === activeIdx ? "scale(1.08)" : "scale(1)",
                                            transition:
                                                "border-color 0.35s ease, transform 0.35s ease, background 0.35s ease",
                                            padding: 0,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {product.urlImagenPrincipal ? (
                                            <img
                                                src={product.urlImagenPrincipal}
                                                alt={product.nombre}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "#f4f5f7",
                                                }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Carousel de productos — solo desktop */}
                {!isSmall && heroProducts.length > 0 && (
                    <div style={{ position: "relative", zIndex: 2, width: 300, flexShrink: 0 }}>
                        {/* Indicadores */}
                        <div
                            style={{
                                display: "flex",
                                gap: 5,
                                justifyContent: "center",
                                marginBottom: 14,
                            }}
                        >
                            {heroProducts.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    style={{
                                        width: i === activeIdx ? 20 : 6,
                                        height: 6,
                                        borderRadius: 999,
                                        border: "none",
                                        background:
                                            i === activeIdx ? "#ffffff" : "rgba(255,255,255,0.30)",
                                        cursor: "pointer",
                                        padding: 0,
                                        transition: "width 0.3s ease, background 0.3s ease",
                                    }}
                                />
                            ))}
                        </div>

                        {/* Card del producto */}
                        {heroProducts.map((product, i) => (
                            <Link
                                key={product.id}
                                to={`/productos/${product.sku}`}
                                onClick={() =>
                                    trackMetric("PRODUCT_CLICK", {
                                        entityId: product.id.toString(),
                                    })
                                }
                                style={{
                                    display: i === activeIdx ? "block" : "none",
                                    textDecoration: "none",
                                    borderRadius: 20,
                                    overflow: "hidden",
                                    background: "rgba(255,255,255,0.10)",
                                    backdropFilter: "blur(16px)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
                                }}
                            >
                                <div
                                    style={{
                                        height: 260,
                                        background: "#f4f5f7",
                                        overflow: "hidden",
                                    }}
                                >
                                    {product.urlImagenPrincipal ? (
                                        <img
                                            src={product.urlImagenPrincipal}
                                            alt={product.nombre}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                background: "#f4f5f7",
                                            }}
                                        />
                                    )}
                                </div>
                                <div style={{ padding: "14px 16px" }}>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: "#ffffff",
                                            letterSpacing: "-0.01em",
                                            textTransform: "uppercase",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {product.nombre}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 700,
                                            color: "#cfe5ff",
                                            marginTop: 4,
                                        }}
                                    >
                                        {product.precio
                                            ? `$${product.precio.toLocaleString("es-AR")}`
                                            : "Consultar precio"}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

const normalizeProducts = (products: Product[], minCount: number): Product[] => {
    if (products.length === 0) return [];
    if (products.length >= minCount) return products;

    const normalized: Product[] = [...products];
    let index = 0;
    while (normalized.length < minCount) {
        normalized.push(products[index % products.length]);
        index++;
    }

    return normalized;
};

function CategoryCard({
    image,
    idx,
    isSmall,
}: {
    image: CategoryImage;
    idx: number;
    isSmall: boolean;
}) {
    // Si la categoría tiene imagen propia, usarla directamente.
    // Solo usar las imágenes de productos (con carousel) cuando no hay imagen configurada.
    const bitTieneImagenPropia = !!image.src;
    const allImages = bitTieneImagenPropia ? [] : (image.productImages ?? []).filter(Boolean);
    const [currentImgIdx, setCurrentImgIdx] = useState(0);
    const [imgOpacity, setImgOpacity] = useState(1);

    useEffect(() => {
        if (allImages.length <= 1) return;
        const interval = setInterval(
            () => {
                setImgOpacity(0);
                setTimeout(() => {
                    setCurrentImgIdx((prev) => (prev + 1) % allImages.length);
                    setImgOpacity(1);
                }, 380);
            },
            3200 + idx * 900,
        );
        return () => clearInterval(interval);
    }, [allImages.length, idx]);

    const currentSrc = bitTieneImagenPropia ? image.src : (allImages[currentImgIdx] ?? "");

    return (
        <Link
            to={image.link}
            onClick={() => trackCategoryClickFromLink(image.link)}
            style={{
                position: "relative",
                display: "block",
                height: isSmall ? 240 : "100%",
                overflow: "hidden",
                borderRadius: isSmall ? 20 : 28,
                textDecoration: "none",
            }}
            onMouseEnter={(e) => {
                const el = e.currentTarget.querySelector(".cat-img") as HTMLElement | null;
                if (el) el.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
                const el = e.currentTarget.querySelector(".cat-img") as HTMLElement | null;
                if (el) el.style.transform = "scale(1)";
            }}
        >
            {currentSrc ? (
                <img
                    className="cat-img"
                    src={currentSrc}
                    alt={image.alt}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: imgOpacity,
                        transition: "opacity 0.38s ease, transform 0.6s cubic-bezier(0.4,0,0.2,1)",
                    }}
                />
            ) : (
                <div
                    className="cat-img"
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            idx === 0
                                ? "linear-gradient(135deg, #003A5C, #00629c)"
                                : "linear-gradient(135deg, #005A8C, #0086c0)",
                        transition: "transform 0.6s cubic-bezier(0.4,0,0.2,1)",
                    }}
                />
            )}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(to top, rgba(0,15,30,0.88) 0%, rgba(0,15,30,0.35) 45%, rgba(0,15,30,0.04) 80%, transparent 100%)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    top: 20,
                    left: 20,
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#ffffff",
                    background: "rgba(255,255,255,0.14)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.22)",
                }}
            >
                {image.title}
            </div>
            {allImages.length > 1 && (
                <div
                    style={{
                        position: "absolute",
                        top: 22,
                        right: 20,
                        display: "flex",
                        gap: 4,
                        alignItems: "center",
                    }}
                >
                    {allImages.map((_, i) => (
                        <div
                            key={i}
                            style={{
                                width: i === currentImgIdx ? 18 : 5,
                                height: 5,
                                borderRadius: 999,
                                background:
                                    i === currentImgIdx ? "#ffffff" : "rgba(255,255,255,0.35)",
                                transition: "width 0.3s ease, background 0.3s ease",
                            }}
                        />
                    ))}
                </div>
            )}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: isSmall ? "28px 22px" : "36px 30px",
                }}
            >
                <div
                    style={{
                        fontSize: isSmall ? 20 : 26,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: "#ffffff",
                        lineHeight: 1.15,
                        marginBottom: 12,
                    }}
                >
                    {image.title}
                </div>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.14)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.22)",
                        color: "#ffffff",
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    Explorar <Icon.arrowUpRight />
                </div>
            </div>
        </Link>
    );
}

function CategorySplitSection({ images }: { images: CategoryImage[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");

    if (images.length === 0) return null;

    const mainImages = images.slice(0, 2);
    const extraImages = images.slice(2, 6);

    return (
        <section style={{ padding: isSmall ? "0 16px 24px" : "0 32px 32px" }}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: isSmall || mainImages.length === 1 ? "1fr" : "1fr 1fr",
                    gap: 12,
                    height: isSmall ? "auto" : 400,
                }}
            >
                {mainImages.map((image, idx) => (
                    <CategoryCard
                        key={image.link || idx}
                        image={image}
                        idx={idx}
                        isSmall={isSmall}
                    />
                ))}
            </div>

            {extraImages.length > 0 && (
                <div
                    style={{
                        marginTop: 12,
                        display: "grid",
                        gridTemplateColumns: isSmall
                            ? "1fr 1fr"
                            : `repeat(${Math.min(extraImages.length, 4)}, 1fr)`,
                        gap: 10,
                    }}
                >
                    {extraImages.map((image, idx) => (
                        <Link
                            key={image.link || idx}
                            to={image.link}
                            onClick={() => trackCategoryClickFromLink(image.link)}
                            style={{
                                position: "relative",
                                display: "block",
                                height: isSmall ? 140 : 180,
                                overflow: "hidden",
                                borderRadius: 16,
                                textDecoration: "none",
                            }}
                        >
                            <ImgPh
                                label={image.alt}
                                src={image.src}
                                ratio="auto"
                                brightness={0.72}
                                style={{ height: "100%", borderRadius: 0 }}
                            />
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    background:
                                        "linear-gradient(to top, rgba(0,15,30,0.75) 0%, transparent 60%)",
                                }}
                            />
                            <div
                                style={{
                                    position: "absolute",
                                    left: 14,
                                    bottom: 13,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#ffffff",
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                {image.title}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </section>
    );
}

function DestacadosSection({ products }: { products: Product[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");
    const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const updateNavState = (swiper: SwiperInstance) => {
        setCanPrev(!swiper.isBeginning);
        setCanNext(!swiper.isEnd);
    };

    return (
        <section style={{ padding: isSmall ? "32px 16px" : "48px 32px" }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 20,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "#00629c",
                            marginBottom: 6,
                        }}
                    >
                        Destacados
                    </div>
                    <h2
                        style={{
                            fontSize: isSmall ? 24 : 30,
                            fontWeight: 700,
                            letterSpacing: "-0.025em",
                            color: "var(--ink-900)",
                            margin: 0,
                            lineHeight: 1.1,
                        }}
                    >
                        Equipos más buscados
                    </h2>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                        type="button"
                        onClick={() => swiperInstance?.slidePrev()}
                        disabled={!canPrev}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            border: "1px solid rgba(191,199,210,0.50)",
                            background: canPrev ? "#ffffff" : "transparent",
                            color: canPrev ? "var(--ink-700)" : "var(--ink-300)",
                            cursor: canPrev ? "pointer" : "default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Icon.arrowLeft />
                    </button>
                    <button
                        type="button"
                        onClick={() => swiperInstance?.slideNext()}
                        disabled={!canNext}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            border: "1px solid rgba(191,199,210,0.50)",
                            background: canNext ? "#ffffff" : "transparent",
                            color: canNext ? "var(--ink-700)" : "var(--ink-300)",
                            cursor: canNext ? "pointer" : "default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Icon.arrowRight />
                    </button>
                    <a
                        href="/productos"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "9px 16px",
                            borderRadius: 999,
                            background: "rgba(0,98,156,0.08)",
                            color: "#00629c",
                            fontSize: 13,
                            fontWeight: 600,
                            textDecoration: "none",
                            border: "1px solid rgba(0,98,156,0.14)",
                        }}
                    >
                        Ver todos <Icon.arrowUpRight />
                    </a>
                </div>
            </div>

            <Swiper
                modules={[Navigation]}
                slidesPerView={2}
                spaceBetween={14}
                breakpoints={{ 900: { slidesPerView: 4 } }}
                onSwiper={(swiper) => {
                    setSwiperInstance(swiper);
                    updateNavState(swiper);
                }}
                onSlideChange={updateNavState}
                onResize={updateNavState}
            >
                {products.map((product, index) => (
                    <SwiperSlide key={`${product.id}-${index}`}>
                        <ProductCard product={product} layout="card" />
                    </SwiperSlide>
                ))}
            </Swiper>
        </section>
    );
}

function NovedadesSection({ products }: { products: Product[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");

    return (
        <section style={{ padding: isSmall ? "32px 16px 48px" : "48px 32px 64px" }}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: isSmall ? "1fr" : "0.9fr 2fr",
                    gap: 14,
                }}
            >
                <div
                    style={{
                        position: "relative",
                        borderRadius: 28,
                        overflow: "hidden",
                        minHeight: isSmall ? 280 : 500,
                        background: "linear-gradient(150deg, #003A5C 0%, #00629c 100%)",
                        color: "#ffffff",
                        padding: isSmall ? "28px 24px" : "36px 32px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage:
                                "radial-gradient(circle at 20% 80%, rgba(0,153,255,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.06) 0%, transparent 50%)",
                        }}
                    />
                    <div style={{ position: "relative" }}>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: "#cfe5ff",
                                marginBottom: 16,
                            }}
                        >
                            Novedades
                        </div>
                        <h3
                            style={{
                                fontSize: isSmall ? 38 : 48,
                                lineHeight: 0.95,
                                margin: 0,
                                fontWeight: 700,
                                letterSpacing: "-0.03em",
                            }}
                        >
                            Lo último
                            <br />
                            <span style={{ color: "#cfe5ff" }}>en purificación</span>
                        </h3>
                        <p
                            style={{
                                fontSize: 14,
                                color: "rgba(255,255,255,0.65)",
                                marginTop: 16,
                                lineHeight: 1.6,
                                maxWidth: 300,
                            }}
                        >
                            Descubrí los equipos y dispensers que acabamos de sumar. Tecnología de
                            purificación para tu hogar u oficina.
                        </p>
                    </div>
                    <div
                        style={{
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                padding: "14px 18px",
                                borderRadius: 14,
                                background: "rgba(255,255,255,0.10)",
                                border: "1px solid rgba(255,255,255,0.15)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.80)" }}>
                                Nuevos ingresos
                            </span>
                            <span
                                style={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    fontFamily: "'SF Mono', monospace",
                                }}
                            >
                                {products.length}
                            </span>
                        </div>
                        <a
                            href="/productos"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                padding: "14px 20px",
                                background: "rgba(255,255,255,0.14)",
                                border: "1px solid rgba(255,255,255,0.25)",
                                borderRadius: 999,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 600,
                                textDecoration: "none",
                                transition: "background 0.2s ease",
                            }}
                        >
                            Ver novedades <Icon.arrowUpRight />
                        </a>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
                    {products.slice(0, 4).map((product, index) => (
                        <ProductCard
                            key={`${product.id}-${index}`}
                            product={product}
                            showDiscount={index === 1}
                            discountText="Nuevo"
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

function EcoSection() {
    const isSmall = useMediaQuery("(max-width:900px)");

    return (
        <section style={{ padding: isSmall ? "0 16px 32px" : "0 32px 40px" }}>
            <div
                style={{
                    borderRadius: isSmall ? 20 : 24,
                    overflow: "hidden",
                    position: "relative",
                    background: "linear-gradient(160deg, #243b2e 0%, #325a42 50%, #243b2e 100%)",
                    padding: isSmall ? "28px 20px" : "36px 48px",
                }}
            >
                {/* Textura sutil */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        backgroundImage:
                            "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        background:
                            "radial-gradient(ellipse at 10% 50%, rgba(60,160,80,0.10) 0%, transparent 50%), radial-gradient(ellipse at 90% 50%, rgba(0,98,156,0.08) 0%, transparent 50%)",
                    }}
                />

                {/* Banner horizontal */}
                <div
                    style={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: isSmall ? "1fr" : "auto 1px 1fr auto",
                        alignItems: "center",
                        gap: isSmall ? 20 : 40,
                    }}
                >
                    {/* Izquierda — número + badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 12,
                                background: "rgba(255,255,255,0.12)",
                                flexShrink: 0,
                            }}
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="rgba(160,230,180,0.90)"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M11 20A7 7 0 0 1 4 13C4 6 11 4 11 4s7 2 7 9a7 7 0 0 1-7 7Z" />
                                <path d="M11 20V12" />
                            </svg>
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: isSmall ? 40 : 52,
                                    fontWeight: 800,
                                    letterSpacing: "-0.04em",
                                    lineHeight: 1,
                                    color: "#ffffff",
                                }}
                            >
                                100%
                            </div>
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: "rgba(160,230,180,0.85)",
                                    marginTop: 2,
                                }}
                            >
                                Eco-Friendly
                            </div>
                        </div>
                    </div>

                    {/* Divisor */}
                    {!isSmall && (
                        <div
                            style={{
                                width: 1,
                                alignSelf: "stretch",
                                background: "rgba(255,255,255,0.15)",
                            }}
                        />
                    )}

                    {/* Centro — texto */}
                    <div>
                        <p
                            style={{
                                fontSize: 13,
                                lineHeight: 1.75,
                                color: "rgba(255,255,255,0.65)",
                                margin: 0,
                            }}
                        >
                            En Voda estamos comprometidos con el medio ambiente y cuidamos la
                            ecología buscando modificar los hábitos de las personas para lograr un
                            equilibrio en el planeta. Contribuimos a disminuir el uso del agua
                            embotellada y con ello reducir los desechos residuales que ocasionan.
                        </p>
                        <p
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                letterSpacing: "-0.01em",
                                color: "#ffffff",
                                margin: "10px 0 0",
                                lineHeight: 1.3,
                            }}
                        >
                            Con VODA, sumate a construir un mundo mejor
                        </p>
                    </div>

                    {/* Derecha — CTAs */}
                    {!isSmall && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                flexShrink: 0,
                            }}
                        >
                            <a
                                href="/productos"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 5,
                                    padding: "10px 20px",
                                    borderRadius: 999,
                                    background: "rgba(160,230,180,0.18)",
                                    color: "#ffffff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    border: "1px solid rgba(160,230,180,0.30)",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Conocer más <Icon.arrowUpRight />
                            </a>
                            <a
                                href="/productos"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 5,
                                    padding: "10px 20px",
                                    borderRadius: 999,
                                    background: "rgba(255,255,255,0.08)",
                                    color: "rgba(255,255,255,0.70)",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                ¡Contrata hoy!
                            </a>
                        </div>
                    )}

                    {/* CTAs mobile */}
                    {isSmall && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <a
                                href="/productos"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "9px 18px",
                                    borderRadius: 999,
                                    background: "rgba(160,230,180,0.18)",
                                    color: "#ffffff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    border: "1px solid rgba(160,230,180,0.30)",
                                }}
                            >
                                Conocer más <Icon.arrowUpRight />
                            </a>
                            <a
                                href="/productos"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "9px 18px",
                                    borderRadius: 999,
                                    background: "rgba(255,255,255,0.08)",
                                    color: "rgba(255,255,255,0.70)",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                }}
                            >
                                ¡Contrata hoy!
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function TrustRow() {
    const isSmall = useMediaQuery("(max-width:900px)");
    const items = [
        { icon: <Icon.truck />, title: "Envíos a todo el país", sub: "Despachamos en 24hs" },
        { icon: <Icon.shield />, title: "Calidad garantizada", sub: "Agua 100% purificada" },
        { icon: <Icon.spark />, title: "Servicio integral", sub: "Instalación y mantenimiento" },
    ];

    return (
        <section style={{ padding: isSmall ? "0 16px 48px" : "0 32px 64px" }}>
            <div
                style={{
                    padding: isSmall ? "20px 20px" : "28px 36px",
                    borderRadius: 24,
                    background: "rgba(255,255,255,0.80)",
                    border: "1px solid rgba(191,199,210,0.40)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 4px 24px rgba(0,58,92,0.06)",
                    display: "grid",
                    gridTemplateColumns: isSmall ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                    gap: isSmall ? 16 : 24,
                }}
            >
                {items.map((item, index) => (
                    <div
                        key={item.title}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                index === 0 ? "flex-start" : index === 1 ? "center" : "flex-end",
                            gap: 14,
                            paddingRight: !isSmall && index < 2 ? 24 : 0,
                            borderRight:
                                !isSmall && index < 2 ? "1px solid rgba(191,199,210,0.40)" : "none",
                        }}
                    >
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 12,
                                background: "rgba(0,98,156,0.08)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#00629c",
                                flexShrink: 0,
                            }}
                        >
                            {item.icon}
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    letterSpacing: "-0.01em",
                                    color: "var(--ink-900)",
                                }}
                            >
                                {item.title}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 2 }}>
                                {item.sub}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const Home: React.FC = () => {
    const { showcaseProducts, novedadesProducts, loading, error } = useHomeProducts();
    const { images: categoryImages } = useCategoryShowcase(2);
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        HttpCache.getPersonTypes();
        HttpCache.getIdentityDocumentTypes();
    }, []);

    useEffect(() => {
        const state = location.state as SnackbarNavState | null;
        const snack = state?.snackbar;

        if (snack?.message) {
            enqueueSnackbar(snack.message, { variant: snack.variant ?? "default" });
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [enqueueSnackbar, location.pathname, location.state, navigate]);

    const featured = useMemo(
        () => [...new Map(showcaseProducts.map((p) => [p.id, p])).values()].slice(0, 8),
        [showcaseProducts],
    );
    const latest = useMemo(() => normalizeProducts(novedadesProducts, 4), [novedadesProducts]);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress size={60} sx={{ color: "#00629c" }} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Cargando productos...
                </Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <Typography variant="h4" color="error" gutterBottom>
                    {error}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    No pudimos cargar los productos en este momento.
                </Typography>
            </Container>
        );
    }

    return (
        <Fragment>
            <HeroSection products={featured} />
            <CategorySplitSection images={categoryImages} />
            {featured.length > 0 && <DestacadosSection products={featured} />}
            <EcoSection />
            {latest.length > 0 && <NovedadesSection products={latest} />}
            <TrustRow />
        </Fragment>
    );
};

export default Home;
